// src/components/CompetitorFeedPage.jsx
// Competitor & Vendor intelligence full feed — replaces the "coming soon" placeholder
// Calls the live api/intel/feed.js endpoint (Watchlist DB + Brave + Haiku synthesis)

import React, { useState, useEffect, useCallback } from 'react';
import './CompetitorFeedPage.css';
import { apiFetch } from '../lib/api';

const TYPE_COLORS = {
  Competitor: { dot: '#ef4444', bg: '#fff5f5', text: '#9b2c2c' },
  Vendor:     { dot: 'var(--dm-blue-mid)', bg: 'var(--dm-blue-pale)', text: '#1e40af' },
  Partner:    { dot: 'var(--dm-green-soft)', bg: '#f0fdf4', text: '#065f46' },
};

// `medium` AND `med`: the prompt asks for "high|medium|low" and the cache stores
// `medium` (47 of 79 items on 24 Oct), but this map only had `med`. Every medium
// item therefore fell through to the `|| REL_COLORS.med` default for its colour
// and, worse, failed the `=== 'med'` label test below and rendered as **"Low"**.
// Both spellings are accepted rather than picking one, because `med` is what
// api/news/feed.js and CommandCenter use for the OTHER news path — normalising
// one side would have desynchronised the other. See P0.29.
const REL_COLORS = { high: '#ef4444', medium: '#f59e0b', med: '#f59e0b', low: '#94a3b8' };

/** The display label for a relevance value, tolerant of both spellings. */
const REL_LABEL = (r) =>
  r === 'high' ? 'High' : (r === 'medium' || r === 'med') ? 'Med' : 'Low';

function IntelCard({ item, onClick }) {
  const typeStyle = TYPE_COLORS[item.type] || TYPE_COLORS.Competitor;

  return (
    <div
      className="cfp-card"
      onClick={() => onClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick(item)}
    >
      <div className="cfp-card-header">
        <span className="cfp-entity-badge" style={{ background: typeStyle.bg, color: typeStyle.text }}>
          <span className="cfp-dot" style={{ background: typeStyle.dot }} />
          {item.entity}
        </span>
        <span className="cfp-type-tag">{item.type}</span>
        <span className="cfp-time">{item.time || ''}</span>
        {item.relevance && (
          <span className="cfp-rel" style={{ color: REL_COLORS[item.relevance] || REL_COLORS.med }}>
            ● {REL_LABEL(item.relevance)}
          </span>
        )}
      </div>
      <p className="cfp-text">{item.text}</p>
      {item.relevanceNote && (
        <p className="cfp-note">💡 {item.relevanceNote}</p>
      )}
      {/* P0.29 29.1 — the angle, which the model may now decline to give.
          Nullable on purpose: an item with no honest route into one of our own
          offerings shows none, rather than the filler the old non-null contract
          forced. `digitalme_angle` is the pre-rename key, still present in
          already-cached items; they are rewritten nightly. */}
      {(item.solution_angle || item.digitalme_angle) && (
        <p className="cfp-angle">🎯 {item.solution_angle || item.digitalme_angle}</p>
      )}
    </div>
  );
}

function IntelModal({ item, onClose }) {
  const typeStyle = TYPE_COLORS[item.type] || TYPE_COLORS.Competitor;
  const [briefing, setBriefing]   = React.useState(null);
  const [loading, setLoading]     = React.useState(true);
  const [error, setError]         = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchBriefing() {
      setLoading(true);
      setError(null);
      try {
        const res  = await apiFetch('/api/intel/briefing', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ entity: item.entity, type: item.type }),
        });
        const data = await res.json();
        if (!cancelled) {
          if (data.success) setBriefing(data.briefing);
          else setError(data.error || 'Could not load briefing');
        }
      } catch (err) {
        if (!cancelled) setError('Could not reach briefing service');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchBriefing();
    return () => { cancelled = true; };
  }, [item.entity, item.type]);

  // Render markdown-like briefing text
  function renderBriefing(text) {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h4 key={i} className="cfp-brief-heading">{line.slice(3)}</h4>;
      }
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return <li key={i} className="cfp-brief-bullet">{line.slice(2)}</li>;
      }
      if (line.trim() === '') return <div key={i} className="cfp-brief-spacer" />;
      return <p key={i} className="cfp-brief-para">{line}</p>;
    });
  }

  return (
    <div className="cfp-overlay" onClick={onClose}>
      <div className="cfp-modal cfp-modal--briefing" onClick={e => e.stopPropagation()}>
        <button className="cfp-modal-close" onClick={onClose}>✕</button>

        {/* Header */}
        <div className="cfp-modal-meta">
          <span className="cfp-entity-badge" style={{ background: typeStyle.bg, color: typeStyle.text }}>
            <span className="cfp-dot" style={{ background: typeStyle.dot }} />
            {item.entity}
          </span>
          <span className="cfp-type-tag">{item.type}</span>
          {item.time && <span className="cfp-time">{item.time}</span>}
        </div>

        {/* One-line summary from feed */}
        <p className="cfp-modal-body">{item.text}</p>
        {item.relevanceNote && (
          <p className="cfp-modal-note">💡 {item.relevanceNote}</p>
        )}
        {(item.solution_angle || item.digitalme_angle) && (
          <p className="cfp-modal-angle">🎯 {item.solution_angle || item.digitalme_angle}</p>
        )}

        {/* Deep briefing */}
        <div className="cfp-briefing-section">
          <div className="cfp-briefing-header">
            <span className="cfp-briefing-label">🔍 Intelligence Briefing</span>
            {!loading && <span className="cfp-briefing-fresh">Just searched</span>}
          </div>
          {loading && (
            <div className="cfp-briefing-loading">
              <div className="cfp-briefing-dots"><span /><span /><span /></div>
              <span>Searching for latest intel...</span>
            </div>
          )}
          {error && <p className="cfp-briefing-error">⚠ {error}</p>}
          {briefing && !loading && (
            <div className="cfp-briefing-body">
              {renderBriefing(briefing)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CompetitorFeedPage() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadFeed = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      if (refresh) {
        // Manual refresh — run the live pipeline and update cache
        await apiFetch('/api/intel/refresh-feed', { method: 'POST' });
      }
      // Always read from cache
      const res  = await apiFetch('/api/intel/feed');
      const data = await res.json();
      if (data.success && Array.isArray(data.items)) {
        setItems(data.items);
        if (data.empty) {
          setLastUpdated('No data yet — first run in progress');
        } else {
          setLastUpdated(data.lastUpdated || new Date().toLocaleTimeString());
        }
      } else {
        setError(data.error || 'Failed to load intel feed');
      }
    } catch (err) {
      setError('Could not reach intel feed. Try refreshing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const types    = ['all', 'Competitor', 'Vendor', 'Partner'];
  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);
  const countFor = t => t === 'all' ? items.length : items.filter(i => i.type === t).length;

  return (
    <div className="cfp-root">
      {/* Header */}
      <div className="cfp-header">
        <div className="cfp-header-left">
          <h1 className="cfp-title">🏁 Competitor & Vendor Intelligence</h1>
          {lastUpdated && <span className="cfp-updated">Updated {lastUpdated}</span>}
        </div>
        <button className="cfp-refresh-btn" onClick={() => loadFeed(true)} disabled={loading}>
          {loading ? 'Loading…' : '↺ Refresh'}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="cfp-filters">
        {types.map(t => (
          <button
            key={t}
            className={`cfp-filter-btn${filter === t ? ' cfp-filter-btn--active' : ''}`}
            onClick={() => setFilter(t)}
          >
            {t === 'all' ? `All (${countFor('all')})` : `${t}s (${countFor(t)})`}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="cfp-loading">
          <div className="cfp-spinner" />
          <p>Scanning competitors and vendors…</p>
        </div>
      ) : error ? (
        <div className="cfp-error">
          <p>{error}</p>
          <button className="cfp-refresh-btn" onClick={() => loadFeed(true)}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="cfp-empty">No intel matches this filter.</div>
      ) : (
        <div className="cfp-grid">
          {filtered.map((item, i) => (
            <IntelCard key={i} item={item} onClick={setExpanded} />
          ))}
        </div>
      )}

      {expanded && <IntelModal item={expanded} onClose={() => setExpanded(null)} />}
    </div>
  );
}
